import { observer } from 'mobx-react';
import { IUnitValue, IUserDashboardWidgetItemModel, UserDashboardWidgetModel } from 'shared/models';

import { formatValueWithUnits } from '../../../../../../utilities/formatValueWithUnits';
import { useDashboardDict } from '../../../../../../context/useDashboardDict';

import classnames from 'classnames';
import { UiHint } from 'shared/uikit/UiHint';
import { useIsTruncated, useMedia } from 'shared/hooks';

import './styles.scss';
import { PPDWidgetPropertiesBadge } from "../PPDWidgetPropertiesBadge";

type StoreId = IUserDashboardWidgetItemModel['store_id'];
type PropsType = {
    items: UserDashboardWidgetModel['items'];
    labels?: UserDashboardWidgetModel['labels'];
    className?: string;
    valueType?: 'main' | 'detailed';
    stickyHeader?: boolean;
    withStatistics?: boolean;
    onStoreStatsClick?: (item: IUserDashboardWidgetItemModel) => void;
};

function pickValue(value?: IUnitValue | IUnitValue[] | null, type: 'main' | 'detailed' = 'main') {
    if (!value) return null;
    if (Array.isArray(value)) {
        const found = value.find(v => v.type === type);
        if (found) return found;
        return value[0] ?? null;
    }
    return value;
}

export const PPDWidgetStatsTable = observer(({
    items = [],
    labels = {},
    className,
    valueType = 'main',
    stickyHeader = false,
    withStatistics = false,
    onStoreStatsClick,
}: PropsType) => {
    const { stores, units } = useDashboardDict();
    const { isMobileOrTablet } = useMedia();

    const getStoreName = (storeId: StoreId) => {
        if (storeId === null) return 'Бутик';

        // Ищем магазин по ID, учитывая возможные типы данных
        const store = stores?.find(s => {
            // Сравниваем как числа, так и строки
            return s.id === storeId || s.id === Number(storeId) || s.id === String(storeId);
        });

        return store?.name ?? 'Бутик';
    };

    const renderValueCell = (value: IUnitValue | IUnitValue[] | null, hint?: string | { main?: string; detailed?: string }) => {
        const val = pickValue(value, valueType);

        let finalHint: string = '';

        if (hint) {
            if (typeof hint === "object") {
                finalHint = hint[valueType] ?? '';
            } else {
                finalHint = Array.isArray(value) ? '' : hint;
            }
        }

        return (
            <span className="ppd-widget-stores-table__accent-value">
                {val ? formatValueWithUnits(val, units) : '—'}
                {finalHint && <UiHint text={finalHint} className="ppd-widget-stores-table__cell-hint"/>}
            </span>
        );
    };

    const renderValueCellWithDirection = (
        fact: IUnitValue | IUnitValue[] | null,
    ) => {
        const factVal = pickValue(fact, valueType);

        // Добавить проверки на null/undefined:
        if (!factVal) return <span>—</span>;

        const isTrendUp = factVal.amount > 0;

        return (
            <span className="ppd-widget-stores-table__accent-value"
                style={{
                    color: isTrendUp
                        ? "#22C55E"
                        : factVal.amount === 0
                            ? "#858991" : "#F43F5E"
                }}
            >
                {factVal ? formatValueWithUnits(factVal, units) : '—'}
                {factVal.amount !== 0 && (
                    <svg
                        width="10"
                        height="11"
                        viewBox="0 0 10 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: !isTrendUp ? 'rotate(180deg)' : '' }}
                    >
                        <path d="M0.916687 5.50008L5.00002 1.41675M5.00002 1.41675L9.08335 5.50008M5.00002 1.41675V9.58341"
                            stroke="currentColor"
                            strokeWidth="1.16667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}

            </span>
        );
    };


    const { ref: storeNameRef, isTruncated } = useIsTruncated<HTMLSpanElement>();

    const renderRow = (item: IUserDashboardWidgetItemModel) => {
        return (
            <tr className="ppd-widget-stats-table__row" key={`${item.store_id}-${item.group_id || 0}`}>
                <td className="ppd-widget-stats-table__cell ppd-widget-stats-table__cell--store">
                    <span className="ppd-widget-stats-table__cell-store-text" ref={storeNameRef}>
                        {getStoreName(item.store_id)}
                    </span>
                    {isTruncated && (
                        <span className="ppd-widget-stats-table__full-store-text">
                            {getStoreName(item.store_id)}
                        </span>
                    )}
                </td>
                <td className="ppd-widget-stats-table__cell">
                    {renderValueCell(
                        item.plan || null,
                        item.plan_hint || ''
                    )}
                </td>

                { withStatistics ? (
                    <>
                        <td className="ppd-widget-stats-table__cell">
                            {renderValueCellWithDirection(item.fact || null)}
                        </td>

                        <td className="ppd-widget-stats-table__cell">
                            {isMobileOrTablet ? (

                                <button
                                    className="ppd-widget-planned-table__link"
                                    onClick={() => onStoreStatsClick?.(item)}
                                >
                                    Статистика
                                </button>

                            ) : (
                                <PPDWidgetPropertiesBadge
                                    statistic={item?.statistic || []}
                                    label_full={labels?.statistic_full}
                                />
                            )}
                        </td>

                    </>
                ) : (
                    <>
                        <td className="ppd-widget-stats-table__cell">
                            {renderValueCell(
                                item.fact || null,
                                item.fact_hint || ''
                            )}
                        </td>

                        <td className="ppd-widget-stats-table__cell">
                            {renderValueCellWithDirection(item.value || null)}
                        </td>
                    </>
                )}

            </tr>
        );
    };

    return (
        <div className={classnames('ppd-widget-stats-table', className)}>
            <div className="ppd-widget-stats-table__wrapper">
                <table className="ppd-widget-stats-table__table">
                    <thead className=
                        {classnames('ppd-widget-stats-table__thead',
                            { 'ppd-widget-stats-table__thead--sticky': stickyHeader })}
                    >
                        <tr className="ppd-widget-stats-table__thead-row">
                            <th className="ppd-widget-stats-table__head">{labels?.store}</th>
                            <th className="ppd-widget-stats-table__head">{labels?.plan}</th>
                            <th className="ppd-widget-stats-table__head">{labels?.fact}</th>
                            <th className="ppd-widget-stats-table__head">
                                {withStatistics ? (labels?.statistics || "Статистика") : (labels?.compare || "Прирост")}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="ppd-widget-stats-table__body">
                        {items?.map(renderRow)}
                    </tbody>
                </table>
            </div>
        </div>
    );
});
